import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { menuAPI, adminAPI, formatError } from "../utils/api";
import { useKitchen } from "../contexts/KitchenContext";
import { useAuth } from "../contexts/AuthContext";
import { useConfirm } from "../hooks/useConfirm";

import {
  Plus, X, Edit, Trash2, ChevronDown, ChevronUp,
  UtensilsCrossed, BookOpen, CalendarDays, Calculator,
  CheckCircle, AlertTriangle, Search, Save, Loader, Leaf, Upload, FileText
} from "lucide-react";

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const MEAL_TYPES = ["breakfast", "lunch", "snack"];
const MEAL_LABELS = { breakfast: "Breakfast", lunch: "Lunch", snack: "Snack" };
const MEAL_COLORS = {
  breakfast: "bg-amber-50 border-amber-200 text-amber-700",
  lunch: "bg-green-50 border-green-200 text-green-700",
  snack: "bg-purple-50 border-purple-200 text-purple-700",
};
const MEAL_BADGE_COLORS = {
  breakfast: "bg-amber-100 text-amber-700",
  lunch:     "bg-green-100 text-green-700",
  snack:     "bg-purple-100 text-purple-700",
};
const MEAL_OPTIONS = ["breakfast", "lunch", "snack"];

// Parse stored category string → array (handles legacy "both" = breakfast+lunch)
function parseCategories(cat) {
  if (!cat) return ["lunch"];
  if (cat === "both") return ["breakfast", "lunch"];
  return cat.split(",").map(s => s.trim()).filter(Boolean);
}
function formatCategories(arr) {
  if (!arr || arr.length === 0) return "lunch";
  return arr.join(",");
}
function CategoryBadges({ category }) {
  return (
    <div className="flex flex-wrap gap-1 mt-1.5">
      {parseCategories(category).map(c => (
        <span key={c} className={`text-xs font-bold px-2 py-0.5 rounded-full capitalize ${MEAL_BADGE_COLORS[c] || "bg-gray-100 text-gray-600"}`}>{c}</span>
      ))}
    </div>
  );
}
const STATUS_COLORS = {
  planned: "bg-blue-50 text-blue-700",
  cooking: "bg-amber-50 text-amber-700",
  served: "bg-green-50 text-green-700",
};

const TABS = [
  { id: "items",      label: "Menu Items",    icon: UtensilsCrossed },
  { id: "recipes",    label: "Recipes",       icon: BookOpen },
  { id: "plan",       label: "Daily Plan",    icon: CalendarDays },
  { id: "calculator", label: "Stock Calc",    icon: Calculator },
  { id: "nutrition",  label: "Nutrition",     icon: Leaf },
];

// ─── HELPERS ──────────────────────────────────────────────────────────────────
function Modal({ title, onClose, children, wide = false }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className={`bg-white rounded-2xl shadow-xl w-full ${wide ? "max-w-3xl" : "max-w-lg"} max-h-[92vh] overflow-y-auto animate-fade-in-up`}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E1DB] sticky top-0 bg-white z-10">
          <h2 className="font-bold text-lg font-[Manrope] text-[#3A322C]">{title}</h2>
          <button onClick={onClose} className="text-[#8A7E73] hover:text-[#3A322C]"><X size={20} /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

function KitchenSelect({ kitchens, value, onChange }) {
  return (
    <select
      className="border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]"
      value={value} onChange={e => onChange(e.target.value)}
    >
      {kitchens.map(k => <option key={k.id} value={k.id}>{k.name}</option>)}
    </select>
  );
}

// ─── MENU ITEMS TAB ──────────────────────────────────────────────────────────
function MenuItemsTab({ kitchens, selectedKitchen }) {
  const { confirm, ConfirmModal } = useConfirm();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const r = await menuAPI.getItems(selectedKitchen ? { kitchen_id: selectedKitchen } : {});
      setItems(r.data || []);
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
    setLoading(false);
  }, [selectedKitchen]);

  useEffect(() => { fetch(); }, [fetch]);

  const save = async () => {
    setError("");
    try {
      const payload = { ...form, kitchen_id: form.kitchen_id || selectedKitchen || null };
      if (modal.mode === "create") await menuAPI.createItem(payload);
      else await menuAPI.updateItem(modal.id, payload);
      setModal(null);
      fetch();
    } catch (e) { setError(formatError(e)); }
  };

  const del = async (id) => {
    if (!await confirm("Remove menu item?")) return;
    await menuAPI.deleteItem(id);
    fetch();
  };

  const filtered = items.filter(i => !search || i.name.toLowerCase().includes(search.toLowerCase()));

  // Multi-select helpers for the form
  const selectedCats = parseCategories(form.category);
  const toggleCat = (cat) => {
    const current = parseCategories(form.category);
    const next = current.includes(cat) ? current.filter(c => c !== cat) : [...current, cat];
    setForm({ ...form, category: formatCategories(next.length ? next : [cat]) });
  };

  const categoryBadge = (cat) => <CategoryBadges category={cat} />;

  return (
    <div className="space-y-4">
      <div className="flex gap-3 items-center flex-wrap">
        <div className="relative flex-1 min-w-48">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A7E73]" />
          <input placeholder="Search menu items..." value={search} onChange={e => setSearch(e.target.value)}
            className="w-full border border-[#E5E1DB] rounded-lg pl-8 pr-3 py-2 text-sm outline-none focus:border-[#E8956D]" />
        </div>
        <button onClick={() => { setForm({ category: "lunch" }); setModal({ mode: "create" }); setError(""); }}
          className="bg-[#E8956D] text-[#3A322C] rounded-lg px-4 py-2 text-sm font-bold flex items-center gap-1.5">
          <Plus size={14} /> Add Menu Item
        </button>
      </div>

      {loading ? <div className="flex justify-center py-12"><Loader className="animate-spin text-[#E8956D]" /></div> : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(item => (
            <div key={item.id} className="bg-white border border-[#E5E1DB] rounded-xl p-4 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1">
                  <h3 className="font-bold text-[#3A322C] text-base leading-tight">{item.name}</h3>
                  <div className="mt-1.5">{categoryBadge(item.category)}</div>
                  {item.description && <p className="text-xs text-[#8A7E73] mt-2 line-clamp-2">{item.description}</p>}
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <button onClick={() => { setForm({ ...item }); setModal({ mode: "edit", id: item.id }); setError(""); }}
                    className="p-1.5 text-[#8A7E73] hover:text-[#E8956D] rounded-lg hover:bg-[#F4F3F0]"><Edit size={14} /></button>
                  <button onClick={() => del(item.id)}
                    className="p-1.5 text-[#8A7E73] hover:text-[#C2624E] rounded-lg hover:bg-[#FBEAE7]"><Trash2 size={14} /></button>
                </div>
              </div>
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="col-span-3 text-center py-16 text-[#8A7E73]">
              <UtensilsCrossed size={48} className="mx-auto mb-3 opacity-20" />
              <p>No menu items yet. Add your first dish!</p>
            </div>
          )}
        </div>
      )}

      {modal && (
        <Modal title={`${modal.mode === "create" ? "Add" : "Edit"} Menu Item`} onClose={() => setModal(null)}>
          <div className="space-y-4">
            <div>
              <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Dish Name *</label>
              <input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]"
                placeholder="e.g. Sambar Rice, Upma, Idli..." value={form.name || ""} onChange={e => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <label className="text-xs font-bold uppercase text-[#8A7E73] mb-2 block">Served At *</label>
              <div className="flex gap-3">
                {MEAL_OPTIONS.map(cat => (
                  <button key={cat} type="button" onClick={() => toggleCat(cat)}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 transition-all font-bold text-sm capitalize ${
                      selectedCats.includes(cat)
                        ? cat === "breakfast" ? "border-amber-400 bg-amber-50 text-amber-700"
                          : cat === "lunch" ? "border-green-400 bg-green-50 text-green-700"
                          : "border-purple-400 bg-purple-50 text-purple-700"
                        : "border-[#E5E1DB] text-[#8A7E73]"
                    }`}>
                    <span className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                      selectedCats.includes(cat)
                        ? cat === "breakfast" ? "border-amber-500 bg-amber-500"
                          : cat === "lunch" ? "border-green-500 bg-green-500"
                          : "border-purple-500 bg-purple-500"
                        : "border-[#C4BAAF]"
                    }`}>
                      {selectedCats.includes(cat) && <span className="text-white text-[10px] font-bold">✓</span>}
                    </span>
                    {cat}
                  </button>
                ))}
              </div>
              <p className="text-xs text-[#8A7E73] mt-1.5">You can select multiple — e.g. Breakfast + Lunch</p>
            </div>
            <div>
              <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Description</label>
              <textarea rows={2} className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D] resize-none"
                placeholder="Brief description of the dish..." value={form.description || ""} onChange={e => setForm({ ...form, description: e.target.value })} />
            </div>
            <div>
              <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Kitchen (leave blank for all)</label>
              <select className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]"
                value={form.kitchen_id || ""} onChange={e => setForm({ ...form, kitchen_id: e.target.value || null })}>
                <option value="">All Kitchens</option>
                {kitchens.map(k => <option key={k.id} value={k.id}>{k.name}</option>)}
              </select>
            </div>
            {error && <p className="text-sm text-[#C2624E] bg-[#FBEAE7] px-3 py-2 rounded-lg">{error}</p>}
            <div className="flex gap-3">
              <button onClick={() => setModal(null)} className="flex-1 border border-[#E5E1DB] rounded-lg py-2.5 text-sm font-bold text-[#5C5147]">Cancel</button>
              <button onClick={save} className="flex-1 bg-[#E8956D] rounded-lg py-2.5 text-sm font-bold text-[#3A322C]">Save</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ─── RECIPES TAB ─────────────────────────────────────────────────────────────
function RecipesTab({ kitchens, selectedKitchen }) {
  const { confirm, ConfirmModal } = useConfirm();
  const [menuItems, setMenuItems] = useState([]);
  const [masterItems, setMasterItems] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({ ingredients: [], default_servings: 100, cooking_steps: [] });
  const [cookingSteps, setCookingSteps] = useState([]);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [ingredientSearch, setIngredientSearch] = useState("");

  // ── Import state ──
  const importFileRef = React.useRef(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importError, setImportError] = useState("");
  const [importPreview, setImportPreview] = useState(null); // extracted recipes array
  const [importConfirming, setImportConfirming] = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const [mi, master, rec] = await Promise.all([
        menuAPI.getItems({}),
        adminAPI.getMasterItems({}),
        menuAPI.getRecipes(selectedKitchen ? { kitchen_id: selectedKitchen } : {})
      ]);
      setMenuItems(mi.data || []);
      setMasterItems(master.data || []);
      setRecipes(rec.data || []);
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
    setLoading(false);
  }, [selectedKitchen]);

  useEffect(() => { fetch(); }, [fetch]);

  const addIngredient = () => {
    setForm(f => ({ ...f, ingredients: [...f.ingredients, { master_item_id: "", quantity_per_serving: 0, unit: "" }] }));
  };

  const updateIngredient = (idx, field, val) => {
    setForm(f => {
      const ings = [...f.ingredients];
      ings[idx] = { ...ings[idx], [field]: val };
      if (field === "master_item_id") {
        const master = masterItems.find(i => i.id === val);
        if (master) ings[idx].unit = master.unit || "";
      }
      return { ...f, ingredients: ings };
    });
  };

  const removeIngredient = (idx) => {
    setForm(f => ({ ...f, ingredients: f.ingredients.filter((_, i) => i !== idx) }));
  };

  const save = async () => {
    setError("");
    if (!form.menu_item_id) { setError("Please select a menu item"); return; }
    if (form.ingredients.length === 0) { setError("Add at least one ingredient"); return; }
    try {
      const payload = {
        ...form,
        kitchen_id: selectedKitchen || null,
        cooking_steps: cookingSteps.filter(s => s.name?.trim() || s.steps?.some(st => st.trim()))
      };
      if (modal.mode === "create") await menuAPI.createRecipe(payload);
      else await menuAPI.updateRecipe(modal.id, payload);
      setModal(null);
      fetch();
    } catch (e) { setError(formatError(e)); }
  };

  const del = async (id) => {
    if (!await confirm("Delete recipe?")) return;
    await menuAPI.deleteRecipe(id);
    fetch();
  };

  const openEdit = (recipe) => {
    setForm({
      menu_item_id: recipe.menu_item_id,
      default_servings: recipe.default_servings || 100,
      ingredients: recipe.ingredients.map(i => ({
        master_item_id: i.master_item_id,
        quantity_per_serving: i.quantity_per_serving,
        unit: i.unit || ""
      })),
      kitchen_id: recipe.kitchen_id
    });
    // Load cooking steps (or migrate old notes into first section)
    if (recipe.cooking_steps && recipe.cooking_steps.length > 0) {
      setCookingSteps(recipe.cooking_steps);
    } else if (recipe.notes) {
      setCookingSteps([{ name: "Cooking Procedure", steps: recipe.notes.split("\n").filter(s => s.trim()) }]);
    } else {
      setCookingSteps([{ name: "Cooking Procedure", steps: [""] }]);
    }
    setModal({ mode: "edit", id: recipe.id });
    setError("");
    setIngredientSearch("");
  };

  const filteredMasterItems = masterItems.filter(i =>
    !ingredientSearch || i.name.toLowerCase().includes(ingredientSearch.toLowerCase())
  );

  const getItemUnit = (id) => masterItems.find(i => i.id === id)?.unit || "";

  // ── Import recipe document handlers ──
  const handleImportFile = async (file) => {
    if (!file) return;
    setImportLoading(true); setImportError(""); setImportPreview(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await menuAPI.parseRecipeDoc(fd);
      setImportPreview(r.data.extracted || []);
    } catch (e) { setImportError(formatError(e)); }
    setImportLoading(false);
    if (importFileRef.current) importFileRef.current.value = "";
  };

  const handleConfirmImport = async () => {
    if (!importPreview?.length) return;
    setImportConfirming(true);
    try {
      await menuAPI.confirmRecipeImport({ recipes: importPreview });
      setImportPreview(null);
      fetch();
    } catch (e) { setImportError(formatError(e)); }
    setImportConfirming(false);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end gap-2 flex-wrap">
        {/* Import Recipes button */}
        <input ref={importFileRef} type="file" accept=".pdf,.docx,.doc" className="hidden"
          id="recipe-import-file"
          onChange={e => handleImportFile(e.target.files[0])}
          data-testid="recipe-import-input" />
        <label htmlFor="recipe-import-file"
          className={`flex items-center gap-1.5 border border-[#E5E1DB] rounded-lg px-4 py-2 text-sm font-bold text-[#5C5147] cursor-pointer hover:bg-[#F4F3F0] transition-colors ${importLoading ? "opacity-60 pointer-events-none" : ""}`}
          data-testid="import-recipes-btn">
          {importLoading ? <Loader size={14} className="animate-spin" /> : <Upload size={14} />}
          {importLoading ? "Parsing…" : "Import Recipes"}
        </label>
        <button onClick={() => { setForm({ ingredients: [], default_servings: 100 }); setCookingSteps([{ name: "Cooking Procedure", steps: [""] }]); setModal({ mode: "create" }); setError(""); setIngredientSearch(""); }}
          className="bg-[#E8956D] text-[#3A322C] rounded-lg px-4 py-2 text-sm font-bold flex items-center gap-1.5">
          <Plus size={14} /> Add Recipe
        </button>
      </div>

      {importError && (
        <div className="bg-[#FBEAE7] text-[#C2624E] rounded-xl px-4 py-3 text-sm flex items-center gap-2">
          <AlertTriangle size={14} /> {importError}
          <button onClick={() => setImportError("")} className="ml-auto text-[#C2624E] hover:opacity-70"><X size={14} /></button>
        </div>
      )}

      {/* Import Preview Modal */}
      {importPreview && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E1DB]">
              <div>
                <h2 className="font-bold text-lg text-[#3A322C]">Import Preview</h2>
                <p className="text-xs text-[#8A7E73] mt-0.5">{importPreview.length} recipe(s) extracted — review and edit before saving.</p>
              </div>
              <button onClick={() => setImportPreview(null)}><X size={20} /></button>
            </div>
            <div className="overflow-y-auto flex-1 divide-y divide-[#F4F3F0]">
              {importPreview.map((rec, ri) => (
                <div key={ri} className="px-6 py-4 space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <input className="font-bold text-[#3A322C] border-b border-transparent hover:border-[#E8956D] focus:border-[#E8956D] outline-none bg-transparent flex-1"
                          value={rec.name || ""}
                          onChange={e => setImportPreview(p => { const n=[...p]; n[ri]={...n[ri], name:e.target.value}; return n; })} />
                        <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${rec.menu_item_matched ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
                          {rec.menu_item_matched ? "Menu item matched" : "Will create menu item"}
                        </span>
                      </div>
                      <div className="flex gap-3 mt-1 text-xs text-[#8A7E73]">
                        <span>{rec.recipe_code}</span>
                        <input className="border-b border-transparent hover:border-[#E8956D] focus:border-[#E8956D] outline-none bg-transparent w-16 text-center font-bold text-[#E8956D]"
                          type="number" min="1"
                          value={rec.base_servings || 1000}
                          onChange={e => setImportPreview(p => { const n=[...p]; n[ri]={...n[ri], base_servings:parseInt(e.target.value)||1000}; return n; })} />
                        <span>servings</span>
                      </div>
                    </div>
                  </div>
                  {rec.notes && <p className="text-xs text-amber-700 bg-amber-50 rounded px-3 py-1.5">{rec.notes}</p>}
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead><tr className="text-left text-[#8A7E73]">
                        <th className="pb-1 pr-3">Ingredient</th><th className="pb-1 pr-3 text-right">Qty</th><th className="pb-1">Unit</th>
                      </tr></thead>
                      <tbody className="divide-y divide-[#F4F3F0]">
                        {(rec.ingredients || []).map((ing, ii) => (
                          <tr key={ii}>
                            <td className="py-1 pr-3 text-[#3A322C]">{ing.item_name}</td>
                            <td className="py-1 pr-3 text-right">
                              <input type="number" step="any" min="0"
                                className="w-16 text-right border-b border-transparent hover:border-[#E8956D] focus:border-[#E8956D] outline-none bg-transparent font-bold text-[#E8956D]"
                                value={ing.quantity ?? 0}
                                onChange={e => setImportPreview(p => {
                                  const n=[...p]; const ings=[...(n[ri].ingredients||[])];
                                  ings[ii]={...ings[ii], quantity: parseFloat(e.target.value)||0};
                                  n[ri]={...n[ri], ingredients:ings}; return n;
                                })} />
                            </td>
                            <td className="py-1 text-[#8A7E73]">{ing.unit}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
            <div className="px-6 py-4 border-t border-[#E5E1DB] flex gap-3">
              <button onClick={() => setImportPreview(null)} className="flex-1 border border-[#E5E1DB] rounded-xl py-2.5 text-sm font-bold text-[#5C5147]">Cancel</button>
              <button onClick={handleConfirmImport} disabled={importConfirming}
                className="flex-1 bg-[#E8956D] text-[#3A322C] rounded-xl py-2.5 text-sm font-bold disabled:opacity-60 flex items-center justify-center gap-2"
                data-testid="confirm-import-btn">
                {importConfirming ? <Loader size={14} className="animate-spin" /> : <Save size={14} />}
                {importConfirming ? "Saving…" : `Save ${importPreview.length} Recipe(s)`}
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? <div className="flex justify-center py-12"><Loader className="animate-spin text-[#E8956D]" /></div> : (
        <div className="space-y-3">
          {recipes.map(recipe => (
            <div key={recipe.id} className="bg-white border border-[#E5E1DB] rounded-xl overflow-hidden">
              <div className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-[#FAFAF8]"
                onClick={() => setExpanded(expanded === recipe.id ? null : recipe.id)}>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-[#E8956D]/10 flex items-center justify-center">
                    <BookOpen size={16} className="text-[#E8956D]" />
                  </div>
                  <div>
                    <p className="font-bold text-[#3A322C]">{recipe.menu_item_name}</p>
                    <p className="text-xs text-[#8A7E73]">
                      {recipe.ingredients?.length || 0} ingredients · {recipe.default_servings || 0} default servings
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={e => { e.stopPropagation(); openEdit(recipe); }} className="p-1.5 text-[#8A7E73] hover:text-[#E8956D] rounded-lg hover:bg-[#F4F3F0]"><Edit size={14} /></button>
                  <button onClick={e => { e.stopPropagation(); del(recipe.id); }} className="p-1.5 text-[#8A7E73] hover:text-[#C2624E] rounded-lg hover:bg-[#FBEAE7]"><Trash2 size={14} /></button>
                  {expanded === recipe.id ? <ChevronUp size={16} className="text-[#8A7E73]" /> : <ChevronDown size={16} className="text-[#8A7E73]" />}
                </div>
              </div>

              {expanded === recipe.id && (
                <div className="border-t border-[#F4F3F0] px-5 py-4">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left">
                          <th className="text-xs font-bold uppercase text-[#8A7E73] pb-2 pr-4">Ingredient</th>
                          <th className="text-xs font-bold uppercase text-[#8A7E73] pb-2 pr-4">Per Serving</th>
                          <th className="text-xs font-bold uppercase text-[#8A7E73] pb-2">Unit</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#F4F3F0]">
                        {recipe.ingredients?.map((ing, i) => (
                          <tr key={`idx-${i}`}>
                            <td className="py-2 pr-4 font-medium text-[#3A322C]">{ing.item_name || "—"}</td>
                            <td className="py-2 pr-4 text-[#E8956D] font-bold">{ing.quantity_per_serving}</td>
                            <td className="py-2 text-[#8A7E73]">{ing.unit}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                {recipe.cooking_steps?.length > 0 && recipe.cooking_steps.some(s => s.steps?.some(st => st.trim())) ? (
                  <div className="mt-4 space-y-4">
                    {recipe.cooking_steps.map((section, si) => (
                      section.steps?.some(st => st.trim()) ? (
                        <div key={si}>
                          <p className="text-xs font-bold uppercase tracking-wider text-[#5C5147] mb-2">{section.name}</p>
                          <ol className="space-y-1.5">
                            {section.steps.filter(st => st.trim()).map((step, i) => (
                              <li key={`idx-${i}`} className="flex gap-3 text-sm text-[#5C5147]">
                                <span className="w-5 h-5 rounded-full bg-[#E8956D]/10 text-[#E8956D] text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{i+1}</span>
                                <span>{step}</span>
                              </li>
                            ))}
                          </ol>
                        </div>
                      ) : null
                    ))}
                  </div>
                ) : recipe.notes ? (
                  <p className="text-xs text-[#8A7E73] mt-3 bg-[#F4F3F0] rounded-lg px-3 py-2">{recipe.notes}</p>
                ) : null}
                </div>
              )}
            </div>
          ))}
          {recipes.length === 0 && (
            <div className="text-center py-16 text-[#8A7E73]">
              <BookOpen size={48} className="mx-auto mb-3 opacity-20" />
              <p>No recipes yet. Create your first recipe!</p>
            </div>
          )}
        </div>
      )}

      {modal && (
        <Modal title={`${modal.mode === "create" ? "Create" : "Edit"} Recipe`} onClose={() => setModal(null)} wide>
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Menu Item *</label>
                <select className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]"
                  value={form.menu_item_id || ""} onChange={e => setForm({ ...form, menu_item_id: e.target.value })}>
                  <option value="">Select dish</option>
                  {menuItems.map(i => <option key={i.id} value={i.id}>{i.name} ({i.category})</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Default No. of Servings</label>
                <input type="number" min="1"
                  className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]"
                  value={form.default_servings || ""} onChange={e => setForm({ ...form, default_servings: parseInt(e.target.value) || 0 })} />
              </div>
            </div>

            {/* Ingredients */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="text-xs font-bold uppercase text-[#8A7E73]">Ingredients</label>
                <div className="flex gap-2">
                  <div className="relative">
                    <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#8A7E73]" />
                    <input placeholder="Filter ingredients..." value={ingredientSearch} onChange={e => setIngredientSearch(e.target.value)}
                      className="border border-[#E5E1DB] rounded-lg pl-7 pr-3 py-1.5 text-xs outline-none focus:border-[#E8956D] w-44" />
                  </div>
                  <button onClick={addIngredient} type="button"
                    className="text-xs bg-[#E8956D]/10 text-[#E8956D] px-3 py-1.5 rounded-lg font-bold hover:bg-[#E8956D]/20 flex items-center gap-1">
                    <Plus size={12} /> Add
                  </button>
                </div>
              </div>

              <div className="border border-[#E5E1DB] rounded-xl overflow-hidden">
                <div className="grid grid-cols-[1fr_120px_80px_32px] gap-2 bg-[#F4F3F0] px-4 py-2.5">
                  <span className="text-xs font-bold uppercase text-[#5C5147]">Ingredient</span>
                  <span className="text-xs font-bold uppercase text-[#5C5147]">Qty/Serving</span>
                  <span className="text-xs font-bold uppercase text-[#5C5147]">Unit</span>
                  <span />
                </div>
                <div className="divide-y divide-[#F4F3F0] max-h-72 overflow-y-auto">
                  {form.ingredients.map((ing, idx) => (
                    <div key={`k-${idx}`} className="grid grid-cols-[1fr_120px_80px_32px] gap-2 px-4 py-2.5 items-center">
                      <select className="border border-[#E5E1DB] rounded-lg px-2 py-1.5 text-xs outline-none focus:border-[#E8956D]"
                        value={ing.master_item_id || ""} onChange={e => updateIngredient(idx, "master_item_id", e.target.value)}>
                        <option value="">Select item</option>
                        {filteredMasterItems.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
                      </select>
                      <input type="number" step="0.001" min="0"
                        className="border border-[#E5E1DB] rounded-lg px-2 py-1.5 text-xs outline-none focus:border-[#E8956D] w-full"
                        value={ing.quantity_per_serving || ""} onChange={e => updateIngredient(idx, "quantity_per_serving", parseFloat(e.target.value) || 0)} />
                      <span className="text-xs text-[#8A7E73] font-medium">{ing.master_item_id ? getItemUnit(ing.master_item_id) : "—"}</span>
                      <button onClick={() => removeIngredient(idx)} className="text-[#C4BAAF] hover:text-[#C2624E]"><X size={14} /></button>
                    </div>
                  ))}
                  {form.ingredients.length === 0 && (
                    <div className="text-center py-6 text-xs text-[#8A7E73]">Click "+ Add" to add ingredients</div>
                  )}
                </div>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="text-xs font-bold uppercase text-[#8A7E73]">Cooking Procedure (Step-by-step)</label>
                <button type="button" onClick={() => setCookingSteps(s => [...s, { name: "", steps: [""] }])}
                  className="text-xs text-[#E8956D] font-bold hover:underline">+ Add Section</button>
              </div>
              <div className="space-y-4">
                {cookingSteps.map((section, si) => (
                  <div key={si} className="border border-[#E5E1DB] rounded-xl overflow-hidden">
                    {/* Section header */}
                    <div className="flex items-center gap-2 px-3 py-2.5 bg-[#F4F3F0] border-b border-[#E5E1DB]">
                      <input
                        className="flex-1 bg-transparent text-sm font-bold text-[#3A322C] outline-none placeholder:text-[#8A7E73]"
                        placeholder="Section name (e.g. Cooking Procedure, Masala Preparation...)"
                        value={section.name}
                        onChange={e => setCookingSteps(s => { const n=[...s]; n[si]={...n[si],name:e.target.value}; return n; })}
                      />
                      {cookingSteps.length > 1 && (
                        <button type="button" onClick={() => setCookingSteps(s => s.filter((_,i)=>i!==si))}
                          className="text-[#C4BAAF] hover:text-[#C2624E] text-xs">✕</button>
                      )}
                    </div>
                    {/* Steps */}
                    <div className="p-3 space-y-2">
                      {section.steps.map((step, stI) => (
                        <div key={stI} className="flex items-start gap-2">
                          <span className="w-6 h-6 rounded-full bg-[#E8956D]/10 text-[#E8956D] text-xs font-bold flex items-center justify-center flex-shrink-0 mt-1.5">{stI+1}</span>
                          <textarea
                            rows={2}
                            className="flex-1 border border-[#E5E1DB] rounded-lg px-2.5 py-1.5 text-sm outline-none focus:border-[#E8956D] resize-none"
                            placeholder={`Step ${stI+1}...`}
                            value={step}
                            onChange={e => setCookingSteps(s => {
                              const n=[...s]; const steps=[...n[si].steps]; steps[stI]=e.target.value; n[si]={...n[si],steps}; return n;
                            })}
                          />
                          {section.steps.length > 1 && (
                            <button type="button" onClick={() => setCookingSteps(s => {
                              const n=[...s]; n[si]={...n[si],steps:n[si].steps.filter((_,i)=>i!==stI)}; return n;
                            })} className="text-[#C4BAAF] hover:text-[#C2624E] mt-2">✕</button>
                          )}
                        </div>
                      ))}
                      <button type="button"
                        onClick={() => setCookingSteps(s => { const n=[...s]; n[si]={...n[si],steps:[...n[si].steps,""]}; return n; })}
                        className="text-xs text-[#E8956D] font-bold hover:underline mt-1">+ Add Step</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {error && <p className="text-sm text-[#C2624E] bg-[#FBEAE7] px-3 py-2 rounded-lg">{error}</p>}
            <div className="flex gap-3">
              <button onClick={() => setModal(null)} className="flex-1 border border-[#E5E1DB] rounded-lg py-2.5 text-sm font-bold text-[#5C5147]">Cancel</button>
              <button onClick={save} className="flex-1 bg-[#E8956D] rounded-lg py-2.5 text-sm font-bold text-[#3A322C] flex items-center justify-center gap-2">
                <Save size={15} /> Save Recipe
              </button>
            </div>
          </div>
        </Modal>
      )}
      {ConfirmModal}
    </div>
  );
}

// ─── DAILY PLAN TAB ──────────────────────────────────────────────────────────
function DailyPlanTab({ kitchens, selectedKitchen }) {
  const { user } = useAuth();
  const [menuItems, setMenuItems] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);
  const [plan, setPlan] = useState({ id: null, breakfast: [], lunch: [], emergency_additions: [], notes: "" });
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  // Emergency addition form state
  const EA_BLANK = { type: "free_entry", menu_item_id: "", menu_item_name: "", additional_child_count: 0, dish_name: "", ingredients: [{ item_name: "", quantity: "", unit: "kg" }], reason: "" };
  const [eaForm, setEaForm] = useState(EA_BLANK);
  const [showEaForm, setShowEaForm] = useState(false);
  const [approving, setApproving] = useState(null);

  const isManager = user?.is_admin || ["Sr. Manager","Manager","Administration","General Manager","Asst. Manager","Semi- Admin","Supervisor"].includes(user?.role_name || "");

  const fetchBase = useCallback(async () => {
    const [mi, rec] = await Promise.all([menuAPI.getItems({}), menuAPI.getRecipes({})]);
    setMenuItems(mi.data || []);
    setRecipes(rec.data || []);
  }, []);

  const loadPlan = useCallback(async () => {
    if (!selectedKitchen) return;
    try {
      const r = await menuAPI.getPlanByDate(selectedDate, selectedKitchen);
      if (r.data) {
        const d = r.data;
        setPlan({
          id: d.id,
          breakfast: (d.breakfast || []).map(x => ({ ...x, child_count: x.child_count || x.servings || 0 })),
          lunch: (d.lunch || []).map(x => ({ ...x, child_count: x.child_count || x.servings || 0 })),
          emergency_additions: d.emergency_additions || [],
          notes: d.notes || "",
          status: d.status,
        });
      } else {
        setPlan({ id: null, breakfast: [], lunch: [], emergency_additions: [], notes: "" });
      }
    } catch { setPlan({ id: null, breakfast: [], lunch: [], emergency_additions: [], notes: "" }); }
  }, [selectedDate, selectedKitchen]);

  useEffect(() => { fetchBase(); }, [fetchBase]);
  useEffect(() => { loadPlan(); }, [loadPlan]);

  const addItem = (meal, menuItemId) => {
    if (!menuItemId) return;
    if (plan[meal].find(x => x.menu_item_id === menuItemId)) return;
    const mi = menuItems.find(i => i.id === menuItemId);
    setPlan(p => ({ ...p, [meal]: [...p[meal], { menu_item_id: menuItemId, menu_item_name: mi?.name || "", child_count: 0 }] }));
  };

  const removeItem = (meal, idx) => setPlan(p => ({ ...p, [meal]: p[meal].filter((_, i) => i !== idx) }));
  const updateCount = (meal, idx, val) => setPlan(p => {
    const arr = [...p[meal]]; arr[idx] = { ...arr[idx], child_count: parseInt(val) || 0 }; return { ...p, [meal]: arr };
  });

  const savePlan = async () => {
    setError(""); setSaving(true);
    try {
      const payload = {
        date: selectedDate,
        kitchen_id: selectedKitchen,
        breakfast: plan.breakfast.map(x => ({ menu_item_id: x.menu_item_id, menu_item_name: x.menu_item_name, child_count: x.child_count })),
        lunch: plan.lunch.map(x => ({ menu_item_id: x.menu_item_id, menu_item_name: x.menu_item_name, child_count: x.child_count })),
        emergency_additions: plan.emergency_additions,
        notes: plan.notes,
      };
      const r = await menuAPI.savePlan(payload);
      setPlan(p => ({ ...p, id: r.data.id }));
      setSuccess("Plan saved!"); setTimeout(() => setSuccess(""), 3000);
    } catch (e) { setError(formatError(e)); }
    setSaving(false);
  };

  const addEmergency = () => {
    const ea = {
      ...eaForm,
      additional_child_count: parseInt(eaForm.additional_child_count) || 0,
      ingredients: eaForm.ingredients.filter(i => i.item_name.trim()),
      status: "pending_approval",
    };
    if (ea.type === "existing_recipe") {
      const recipe = recipes.find(r => r.menu_item_id === ea.menu_item_id);
      ea.menu_item_name = recipe?.menu_item_name || ea.menu_item_name;
    }
    setPlan(p => ({ ...p, emergency_additions: [...(p.emergency_additions || []), ea] }));
    setEaForm(EA_BLANK); setShowEaForm(false);
    // Auto-save the plan with new emergency addition
    setTimeout(savePlan, 100);
  };

  const removeEmergency = (idx) => setPlan(p => ({ ...p, emergency_additions: (p.emergency_additions || []).filter((_, i) => i !== idx) }));

  const approveEmergency = async (idx) => {
    if (!plan.id) { alert("Save plan first"); return; }
    setApproving(idx);
    try {
      await menuAPI.approvePlanEmergency(plan.id, idx);
      await loadPlan();
    } catch (e) { alert(formatError(e)); }
    setApproving(null);
  };

  const MealSection = ({ meal, label, color }) => {
    const used = plan[meal].map(x => x.menu_item_id);
    const total = plan[meal].reduce((s, x) => s + (x.child_count || 0), 0);
    return (
      <div className={`border-2 rounded-2xl overflow-hidden ${color.border}`} data-testid={`meal-section-${meal}`}>
        <div className={`px-5 py-3 ${color.bg} flex items-center justify-between`}>
          <div>
            <h3 className={`font-bold text-base ${color.text}`}>{label}</h3>
            <p className={`text-xs ${color.text} opacity-70`}>{total.toLocaleString()} children total</p>
          </div>
        </div>
        <div className="bg-white p-4 space-y-2 min-h-[120px]">
          {plan[meal].map((item, idx) => (
            <div key={`${meal}-${idx}`} className="flex items-center gap-2 bg-[#FAFAF8] border border-[#E5E1DB] rounded-xl px-3 py-2.5">
              <span className="text-sm font-medium text-[#3A322C] flex-1 truncate">{item.menu_item_name}</span>
              <div className="flex items-center gap-1">
                <input type="number" min="0" placeholder="Children"
                  className="border border-[#E5E1DB] rounded-lg px-2 py-1 text-sm w-24 text-center outline-none focus:border-[#E8956D] font-bold"
                  value={item.child_count || ""} onChange={e => updateCount(meal, idx, e.target.value)} />
                <span className="text-xs text-[#8A7E73]">children</span>
              </div>
              <button onClick={() => removeItem(meal, idx)} className="text-[#C4BAAF] hover:text-[#C2624E] ml-1"><X size={14} /></button>
            </div>
          ))}
          <select className="w-full border border-dashed border-[#E5E1DB] rounded-xl px-3 py-2 text-sm outline-none focus:border-[#E8956D] text-[#8A7E73]"
            value="" onChange={e => { addItem(meal, e.target.value); e.target.value = ""; }}>
            <option value="">+ Add menu item…</option>
            {menuItems.filter(i => !used.includes(i.id)).map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
          </select>
        </div>
      </div>
    );
  };

  const kitchenName = kitchens.find(k => k.id === selectedKitchen)?.name || "—";

  return (
    <div className="space-y-5" data-testid="daily-plan-tab">
      {/* Header row */}
      <div className="flex gap-3 items-end flex-wrap">
        <div>
          <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Date</label>
          <input type="date" className="border border-[#E5E1DB] rounded-xl px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]"
            value={selectedDate} onChange={e => setSelectedDate(e.target.value)} />
        </div>
        <div>
          <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Kitchen</label>
          <div className="border border-[#E5E1DB] rounded-xl px-3 py-2.5 text-sm bg-[#F4F3F0] font-medium text-[#3A322C] min-w-[180px]">{kitchenName}</div>
        </div>
        <div className="ml-auto flex items-center gap-3">
          {success && <span className="text-sm text-[#637C64] bg-[#EDF2EE] px-3 py-2 rounded-lg">{success}</span>}
          {error && <span className="text-sm text-[#C2624E] bg-[#FBEAE7] px-3 py-2 rounded-lg">{error}</span>}
          {!selectedKitchen && <span className="text-sm text-[#8A7E73]">Select a kitchen first</span>}
          <button onClick={savePlan} disabled={saving || !selectedKitchen}
            className="bg-[#E8956D] text-[#3A322C] rounded-xl px-5 py-2.5 text-sm font-bold flex items-center gap-2 disabled:opacity-60"
            data-testid="save-plan-btn">
            {saving ? <Loader size={14} className="animate-spin" /> : <Save size={14} />} Save Plan
          </button>
        </div>
      </div>

      {/* Breakfast + Lunch sections */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <MealSection meal="breakfast" label="BREAKFAST" color={{ border: "border-amber-200", bg: "bg-amber-50", text: "text-amber-800" }} />
        <MealSection meal="lunch" label="LUNCH" color={{ border: "border-green-200", bg: "bg-green-50", text: "text-green-800" }} />
      </div>

      {/* Notes */}
      <div>
        <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Notes</label>
        <textarea rows={2} className="w-full border border-[#E5E1DB] rounded-xl px-3 py-2.5 text-sm outline-none focus:border-[#E8956D] resize-none"
          placeholder="Special instructions…" value={plan.notes} onChange={e => setPlan(p => ({ ...p, notes: e.target.value }))} />
      </div>

      {/* Emergency Additions */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-bold text-[#3A322C]">Emergency / Substitution Additions</h3>
            <p className="text-xs text-[#8A7E73]">Add mid-production substitutions or failed-batch replacements. Requires manager approval before stock can be issued.</p>
          </div>
          <button onClick={() => setShowEaForm(!showEaForm)}
            className="flex items-center gap-1.5 border border-orange-300 text-orange-700 bg-orange-50 rounded-xl px-4 py-2 text-sm font-bold hover:bg-orange-100 transition-colors"
            data-testid="add-emergency-btn">
            <Plus size={14} /> Add Emergency Item
          </button>
        </div>

        {showEaForm && (
          <div className="bg-orange-50 border border-orange-200 rounded-2xl p-5 space-y-4">
            <h4 className="font-bold text-orange-800 text-sm">New Emergency Addition</h4>
            <div className="flex gap-3">
              {["existing_recipe","free_entry"].map(t => (
                <button key={t} onClick={() => setEaForm(f => ({ ...f, type: t }))}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold border ${eaForm.type === t ? "bg-orange-600 text-white border-orange-600" : "border-orange-300 text-orange-700"}`}>
                  {t === "existing_recipe" ? "Existing Recipe" : "Free Entry"}
                </button>
              ))}
            </div>
            {eaForm.type === "existing_recipe" ? (
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Select Recipe</label>
                  <select className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none"
                    value={eaForm.menu_item_id} onChange={e => setEaForm(f => ({ ...f, menu_item_id: e.target.value }))}>
                    <option value="">Select…</option>
                    {recipes.map(r => <option key={r.id} value={r.menu_item_id}>{r.menu_item_name}</option>)}
                  </select></div>
                <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Extra Children</label>
                  <input type="number" min="0" className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none"
                    value={eaForm.additional_child_count} onChange={e => setEaForm(f => ({ ...f, additional_child_count: e.target.value }))} /></div>
              </div>
            ) : (
              <div className="space-y-3">
                <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Dish Name</label>
                  <input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none"
                    placeholder="e.g. Upma" value={eaForm.dish_name} onChange={e => setEaForm(f => ({ ...f, dish_name: e.target.value }))} /></div>
                <div>
                  <label className="text-xs font-bold uppercase text-[#8A7E73] mb-2 block">Ingredients</label>
                  {eaForm.ingredients.map((ing, ii) => (
                    <div key={ii} className="flex gap-2 mb-2">
                      <input className="flex-1 border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none" placeholder="Ingredient name"
                        value={ing.item_name} onChange={e => setEaForm(f => { const ings=[...f.ingredients]; ings[ii]={...ings[ii],item_name:e.target.value}; return {...f,ingredients:ings}; })} />
                      <input type="number" className="w-20 border border-[#E5E1DB] rounded-lg px-2 py-2 text-sm outline-none text-center" placeholder="Qty"
                        value={ing.quantity} onChange={e => setEaForm(f => { const ings=[...f.ingredients]; ings[ii]={...ings[ii],quantity:e.target.value}; return {...f,ingredients:ings}; })} />
                      <select className="w-20 border border-[#E5E1DB] rounded-lg px-2 py-2 text-sm outline-none"
                        value={ing.unit} onChange={e => setEaForm(f => { const ings=[...f.ingredients]; ings[ii]={...ings[ii],unit:e.target.value}; return {...f,ingredients:ings}; })}>
                        <option value="kg">kg</option><option value="Litres">L</option><option value="Kg's">Kg's</option>
                      </select>
                      {eaForm.ingredients.length > 1 && <button onClick={() => setEaForm(f => ({ ...f, ingredients: f.ingredients.filter((_,i)=>i!==ii) }))} className="text-red-400 hover:text-red-600"><X size={14} /></button>}
                    </div>
                  ))}
                  <button onClick={() => setEaForm(f => ({ ...f, ingredients: [...f.ingredients, { item_name:"",quantity:"",unit:"kg" }] }))}
                    className="text-xs text-orange-600 font-bold hover:underline">+ Add ingredient</button>
                </div>
              </div>
            )}
            <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Reason *</label>
              <input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none"
                placeholder="e.g. Batch failure — substituting Sambar with Dalcha"
                value={eaForm.reason} onChange={e => setEaForm(f => ({ ...f, reason: e.target.value }))} /></div>
            <div className="flex gap-3">
              <button onClick={() => setShowEaForm(false)} className="flex-1 border border-[#E5E1DB] rounded-xl py-2 text-sm font-bold text-[#5C5147]">Cancel</button>
              <button onClick={addEmergency} disabled={!selectedKitchen || (!eaForm.dish_name && !eaForm.menu_item_id) || !eaForm.reason}
                className="flex-1 bg-orange-600 text-white rounded-xl py-2 text-sm font-bold disabled:opacity-50"
                data-testid="save-emergency-btn">Add & Save</button>
            </div>
          </div>
        )}

        {(plan.emergency_additions || []).map((ea, idx) => (
          <div key={idx} className={`border-2 rounded-xl p-4 ${ea.status === "approved" ? "border-green-300 bg-green-50" : "border-orange-300 bg-orange-50"}`}
            data-testid={`emergency-item-${idx}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-bold text-[#3A322C] text-sm">
                    {ea.type === "existing_recipe" ? ea.menu_item_name : ea.dish_name}
                  </span>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${ea.status === "approved" ? "bg-green-200 text-green-800" : "bg-orange-200 text-orange-800"}`}>
                    {ea.status === "approved" ? "Approved" : "Pending Approval"}
                  </span>
                  {ea.type === "existing_recipe" && <span className="text-xs text-[#8A7E73]">{ea.additional_child_count} extra children</span>}
                </div>
                {ea.reason && <p className="text-xs text-[#8A7E73] mt-1">Reason: {ea.reason}</p>}
                {ea.approved_by && <p className="text-xs text-green-700 mt-0.5">Approved by: {ea.approved_by}</p>}
                {ea.type === "free_entry" && ea.ingredients?.length > 0 && (
                  <p className="text-xs text-[#5C5147] mt-1">{ea.ingredients.map(i => `${i.item_name} ${i.quantity}${i.unit}`).join(", ")}</p>
                )}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {isManager && ea.status !== "approved" && (
                  <button onClick={() => approveEmergency(idx)} disabled={approving === idx}
                    className="text-xs bg-green-600 text-white px-3 py-1.5 rounded-lg font-bold hover:bg-green-700 disabled:opacity-60 flex items-center gap-1"
                    data-testid={`approve-emergency-${idx}`}>
                    {approving === idx ? <Loader size={12} className="animate-spin" /> : <CheckCircle size={12} />} Approve
                  </button>
                )}
                <button onClick={() => removeEmergency(idx)} className="text-[#C4BAAF] hover:text-[#C2624E]"><X size={14} /></button>
              </div>
            </div>
          </div>
        ))}

        {(plan.emergency_additions || []).length === 0 && !showEaForm && (
          <p className="text-xs text-[#C4BAAF] text-center py-3">No emergency additions for this date.</p>
        )}
      </div>
    </div>
  );
}

// ─── STOCK CALCULATOR TAB ─────────────────────────────────────────────────────
function StockCalculatorTab({ kitchens, selectedKitchen }) {
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const calculate = async () => {
    if (!selectedKitchen) { setError("Please select a kitchen"); return; }
    setError(""); setLoading(true);
    try {
      const r = await menuAPI.calculateStock(date, selectedKitchen);
      setResult(r.data);
    } catch (e) { setError(formatError(e)); }
    setLoading(false);
  };

  return (
    <div className="space-y-5">
      <div className="bg-white border border-[#E5E1DB] rounded-xl p-5 space-y-4">
        <div className="flex gap-4 flex-wrap items-end">
          <div>
            <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Date</label>
            <input type="date" className="border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]"
              value={date} onChange={e => setDate(e.target.value)} />
          </div>
          <button onClick={calculate} disabled={loading}
            className="bg-[#E8956D] text-[#3A322C] rounded-lg px-6 py-2.5 text-sm font-bold flex items-center gap-2 disabled:opacity-60">
            {loading ? <Loader size={14} className="animate-spin" /> : <Calculator size={14} />} Calculate Stock Needed
          </button>
        </div>
        {error && <p className="text-sm text-[#C2624E] bg-[#FBEAE7] px-3 py-2 rounded-lg">{error}</p>}
        <p className="text-xs text-[#8A7E73]">This calculates total ingredients needed based on the Daily Plan for the selected date, and compares with current stock levels.</p>
      </div>

      {result && (
        <div className="space-y-4">
          {/* Summary */}
          <div className={`rounded-xl px-5 py-4 flex items-center gap-4 ${result.all_sufficient ? "bg-[#EDF2EE] border border-[#637C64]/30" : "bg-[#FBEAE7] border border-[#C2624E]/30"}`}>
            {result.all_sufficient ? (
              <><CheckCircle size={24} className="text-[#637C64]" /><div><p className="font-bold text-[#637C64]">All ingredients available</p><p className="text-sm text-[#637C64]/80">Stock is sufficient for the planned menu on {new Date(date + "T00:00:00").toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" })}</p></div></>
            ) : (
              <><AlertTriangle size={24} className="text-[#C2624E]" /><div><p className="font-bold text-[#C2624E]">{result.deficit_count} ingredient(s) below required level</p><p className="text-sm text-[#C2624E]/80">Please check the items marked in red below and raise indents accordingly.</p></div></>
            )}
          </div>

          {result.ingredients?.length > 0 ? (
            <div className="bg-white border border-[#E5E1DB] rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#F4F3F0]">
                    <tr>
                      {["Ingredient", "Required", "Available", "Deficit", "Status"].map(h => (
                        <th key={h} className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-[#5C5147]">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#F4F3F0]">
                    {result.ingredients.map((ing, i) => (
                      <tr key={`idx-${i}`} className={ing.is_emergency ? "bg-orange-50/50 hover:bg-orange-50" : (ing.is_sufficient ? "hover:bg-[#FAFAF8]" : "bg-[#FBEAE7]/30 hover:bg-[#FBEAE7]/50")}>
                        <td className="px-4 py-3 font-medium text-[#3A322C]">
                          {ing.item_name}
                          {ing.is_emergency && <span className="ml-2 text-[10px] font-bold bg-orange-200 text-orange-800 px-1.5 py-0.5 rounded-full">EMERGENCY{ing.emergency_status === "pending_approval" ? " (Pending)" : ""}</span>}
                        </td>
                        <td className="px-4 py-3 font-bold text-[#5C5147]">{ing.total_qty_needed} {ing.unit}</td>
                        <td className="px-4 py-3">
                          <span className={ing.is_sufficient ? "text-[#637C64] font-semibold" : "text-[#C2624E] font-semibold"}>
                            {ing.current_stock} {ing.unit}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {!ing.is_sufficient ? (
                            <span className="font-bold text-[#C2624E]">-{ing.deficit} {ing.unit}</span>
                          ) : <span className="text-[#C4BAAF]">—</span>}
                        </td>
                        <td className="px-4 py-3">
                          {ing.is_sufficient ? (
                            <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-[#EDF2EE] text-[#637C64] flex items-center gap-1 w-fit">
                              <CheckCircle size={11} /> OK
                            </span>
                          ) : (
                            <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-[#FBEAE7] text-[#C2624E] flex items-center gap-1 w-fit">
                              <AlertTriangle size={11} /> Low
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="text-center py-10 text-[#8A7E73] bg-white border border-[#E5E1DB] rounded-xl">
              <CalendarDays size={40} className="mx-auto mb-3 opacity-20" />
              <p>No daily plan found for this date. Create a plan first.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────
export default function MenuRecipes() {
  const { kitchens: ctxKitchens, selectedKitchen, changeKitchen } = useKitchen();
  const [kitchens, setKitchens] = useState(ctxKitchens || []);
  // Fallback: direct fetch if context kitchens still empty
  useEffect(() => {
    if (ctxKitchens && ctxKitchens.length > 0) { setKitchens(ctxKitchens); return; }
    const _t = setTimeout(() => {
      if (kitchens.length > 0) return;
      import("../utils/api").then(({ usersAPI }) => {
        usersAPI.getKitchens().then(r => {
          const ks = r.data || [];
          if (ks.length > 0) setKitchens(ks);
        }).catch(() => {});
      });
    }, 800);
    return () => clearTimeout(_t);
  }, [ctxKitchens]);

  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "items");
  useEffect(() => { const t = searchParams.get("tab"); if (t) setActiveTab(t); }, [searchParams]);

  return (
    <div className="space-y-6 max-w-6xl" data-testid="menu-recipes">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-extrabold font-[Manrope] text-[#3A322C]">Menu & Recipe Management</h1>
          <p className="text-sm text-[#8A7E73] mt-1">Manage dishes, recipes, daily plans and stock requirements</p>
        </div>
        <KitchenSelect kitchens={kitchens} value={selectedKitchen} onChange={changeKitchen} />
      </div>

      {/* Tabs */}
      <div className="flex border-b border-[#E5E1DB] overflow-x-auto">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            className={`px-5 py-3 text-sm font-semibold flex items-center gap-2 whitespace-nowrap transition-colors -mb-px ${activeTab === t.id ? "border-b-2 border-[#E8956D] text-[#E8956D]" : "text-[#8A7E73] hover:text-[#5C5147]"}`}>
            <t.icon size={15} strokeWidth={2} />{t.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === "items" && <MenuItemsTab kitchens={kitchens} selectedKitchen={selectedKitchen} />}
        {activeTab === "recipes" && <RecipesTab kitchens={kitchens} selectedKitchen={selectedKitchen} />}
        {activeTab === "plan" && <DailyPlanTab kitchens={kitchens} selectedKitchen={selectedKitchen} />}
        {activeTab === "calculator" && <StockCalculatorTab kitchens={kitchens} selectedKitchen={selectedKitchen} />}
        {activeTab === "nutrition" && <NutritionTab />}
      </div>
    </div>
  );
}


// ─── NUTRITION CALCULATOR TAB ────────────────────────────────────────────────
const ICMR_DAILY = { calories: 1670, protein: 22, carbs: 270, fat: 40, iron: 13, calcium: 600 };
const ICMR_MDM   = { calories: 557,  protein: 7.3, carbs: 90, fat: 13.3, iron: 4.3, calcium: 200 };

const NUTRIENT_CONFIG = [
  { key: "calories", label: "Calories",  unit: "kcal", color: "bg-amber-500"   },
  { key: "protein",  label: "Protein",   unit: "g",    color: "bg-blue-500"    },
  { key: "carbs",    label: "Carbs",     unit: "g",    color: "bg-orange-400"  },
  { key: "fat",      label: "Fat",       unit: "g",    color: "bg-yellow-500"  },
  { key: "iron",     label: "Iron",      unit: "mg",   color: "bg-red-500"     },
  { key: "calcium",  label: "Calcium",   unit: "mg",   color: "bg-purple-500"  },
];

function NutritionBar({ pct, color }) {
  const capped = Math.min(pct, 120);
  return (
    <div className="flex-1 h-2.5 bg-[#F4F3F0] rounded-full overflow-hidden">
      <div className={`h-full rounded-full transition-all ${pct >= 80 ? color : pct >= 50 ? "bg-amber-400" : "bg-[#C4BAAF]"}`}
        style={{ width: `${Math.min(capped, 100)}%` }} />
    </div>
  );
}

function NutritionTab() {
  const [recipes, setRecipes]       = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [servings, setServings]     = useState(100);
  const [result, setResult]         = useState(null);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState("");
  const [reference, setReference]   = useState("mdm"); // "mdm" | "daily"

  useEffect(() => {
    menuAPI.getRecipes().then(r => setRecipes(r.data || [])).catch(() => {});
  }, []);

  const calculate = async () => {
    setLoading(true); setError(""); setResult(null);
    try {
      const r = await menuAPI.calcNutrition({ recipe_id: selectedId || undefined, servings });
      setResult(r.data);
    } catch (e) { setError(formatError(e)); }
    setLoading(false);
  };

  const ref = reference === "mdm" ? ICMR_MDM : ICMR_DAILY;

  return (
    <div className="space-y-5 max-w-5xl">
      <div className="bg-[#FDF4EB] border border-[#D98032]/30 rounded-xl px-4 py-3 text-sm text-[#5C5147]">
        <strong className="text-[#D98032]">ICMR Reference Values</strong> — Daily for school children (6–12 yrs):
        {Object.entries(ICMR_DAILY).map(([k, v]) => {
          const nc = NUTRIENT_CONFIG.find(n => n.key === k);
          return <span key={k} className="ml-3 text-xs font-bold">{nc?.label}: {v}{nc?.unit}</span>;
        })}
      </div>

      {/* Controls */}
      <div className="flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-52">
          <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Recipe</label>
          <select value={selectedId} onChange={e => setSelectedId(e.target.value)}
            className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]">
            <option value="">All Recipes</option>
            {recipes.map(r => <option key={r.id} value={r.id}>{r.item_name}</option>)}
          </select>
        </div>
        <div className="w-36">
          <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Servings</label>
          <input type="number" min={1} value={servings} onChange={e => setServings(Number(e.target.value))}
            className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]" />
        </div>
        <div>
          <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Compare with</label>
          <div className="flex gap-1 bg-[#F4F3F0] p-1 rounded-lg">
            {[["mdm","MDM (1/3 day)"],["daily","Full Day"]].map(([v,l]) => (
              <button key={v} onClick={() => setReference(v)}
                className={`px-3 py-1 rounded-md text-xs font-bold ${reference===v ? "bg-white text-[#3A322C] shadow-sm" : "text-[#8A7E73]"}`}>{l}</button>
            ))}
          </div>
        </div>
        <button onClick={calculate} disabled={loading}
          className="bg-[#E8956D] text-[#3A322C] rounded-lg px-5 py-2 text-sm font-bold hover:bg-[#D48661] disabled:opacity-60 flex items-center gap-2">
          {loading ? <Loader size={14} className="animate-spin" /> : <Leaf size={14} />} Calculate
        </button>
      </div>

      {error && <div className="bg-[#FBEAE7] text-[#C2624E] rounded-xl px-4 py-3 text-sm">{error}</div>}

      {result && (
        <div className="space-y-4">
          {result.results.map((rec, ri) => (
            <div key={ri} className="bg-white border border-[#E5E1DB] rounded-2xl overflow-hidden">
              {/* Header */}
              <div className="px-5 py-3 bg-[#F4F3F0] border-b border-[#E5E1DB] flex items-center justify-between">
                <div>
                  <h3 className="font-bold text-[#3A322C]">{rec.recipe_name || "All Recipes Combined"}</h3>
                  <p className="text-xs text-[#8A7E73]">{servings} servings · Per serving nutritional breakdown</p>
                </div>
                {!rec.has_full_data && (
                  <span className="text-xs text-[#D98032] bg-[#FDF4EB] px-2 py-1 rounded-lg font-bold flex items-center gap-1">
                    <AlertTriangle size={11} /> Some items missing nutrition data
                  </span>
                )}
              </div>

              <div className="p-5 space-y-4">
                {/* Nutrient bars — per serving vs reference */}
                <div className="space-y-3">
                  {NUTRIENT_CONFIG.map(nc => {
                    const val = rec.per_serving[nc.key] || 0;
                    const refVal = ref[nc.key] || 1;
                    const pct = Math.round(val / refVal * 100);
                    return (
                      <div key={nc.key} className="flex items-center gap-3">
                        <span className="text-xs font-bold text-[#5C5147] w-16 shrink-0">{nc.label}</span>
                        <NutritionBar pct={pct} color={nc.color} />
                        <span className="text-xs font-bold text-[#3A322C] w-20 text-right shrink-0">{val} {nc.unit}</span>
                        <span className={`text-xs font-bold w-12 text-right shrink-0 ${pct >= 80 ? "text-[#637C64]" : pct >= 50 ? "text-[#D98032]" : "text-[#C4BAAF]"}`}>
                          {pct}%
                        </span>
                      </div>
                    );
                  })}
                </div>
                <p className="text-[10px] text-[#8A7E73]">% of {reference === "mdm" ? "ICMR MDM recommendation (1/3 of daily)" : "ICMR full-day recommendation"}</p>

                {/* Summary grid */}
                <div className="grid grid-cols-3 md:grid-cols-6 gap-2 pt-2 border-t border-[#F4F3F0]">
                  {NUTRIENT_CONFIG.map(nc => (
                    <div key={nc.key} className="text-center bg-[#FAFAF8] rounded-xl p-3">
                      <p className="text-[10px] text-[#8A7E73] uppercase font-bold mb-0.5">{nc.label}</p>
                      <p className="text-base font-extrabold text-[#3A322C]">{rec.per_serving[nc.key]}</p>
                      <p className="text-[10px] text-[#C4BAAF]">{nc.unit}/serving</p>
                    </div>
                  ))}
                </div>

                {/* Ingredient contributions */}
                {rec.ingredients?.length > 0 && (
                  <details className="mt-2">
                    <summary className="text-xs font-bold text-[#8A7E73] cursor-pointer hover:text-[#5C5147] select-none">
                      View ingredient-wise breakdown ({rec.ingredients.length} items)
                    </summary>
                    <div className="mt-2 overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-[#F4F3F0]">
                          <tr>
                            <th className="text-left px-3 py-2 font-bold text-[#5C5147]">Ingredient</th>
                            <th className="text-right px-3 py-2 font-bold text-[#5C5147]">Qty</th>
                            {NUTRIENT_CONFIG.map(nc => (
                              <th key={nc.key} className="text-right px-3 py-2 font-bold text-[#5C5147]">{nc.label}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-[#F4F3F0]">
                          {rec.ingredients.map((ing, i) => (
                            <tr key={`idx-${i}`} className={`hover:bg-[#FAFAF8] ${!ing.has_data ? "opacity-50" : ""}`}>
                              <td className="px-3 py-2 font-medium text-[#3A322C]">
                                {ing.item_name}
                                {!ing.has_data && <span className="text-[#D98032] ml-1 text-[10px]">(no data)</span>}
                              </td>
                              <td className="px-3 py-2 text-right text-[#8A7E73]">{ing.qty_per_serving} {ing.unit}</td>
                              {NUTRIENT_CONFIG.map(nc => (
                                <td key={nc.key} className="px-3 py-2 text-right text-[#5C5147]">
                                  {ing.contribution ? ing.contribution[nc.key] : "—"}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </details>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* How to add nutrition data hint */}
      <div className="bg-[#F4F3F0] rounded-xl px-4 py-3 text-xs text-[#8A7E73]">
        <strong className="text-[#5C5147]">Missing nutrition data?</strong> Go to <strong>Admin Settings → Master Items</strong> and edit any ingredient to add its nutritional values (calories, protein, carbs, fat, iron, calcium per 100g). ICMR values for common ingredients are pre-loaded.
      </div>
    </div>
  );
}
